import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, demoOeffnen, demostand, fehlertext, type SessionUser } from "./api";
import { Knopf } from "./ui";

/**
 * Die Vorfuehranlage in der Oberflaeche.
 *
 * Zwei Teile, und beide sind Verkaufswerkzeuge und nicht Technik.
 *
 * **Der Einstieg** auf der Anmeldemaske. Ein Knopf statt einer Mailadresse und
 * eines Passworts. Zwei Rollen zur Auswahl, weil im Gespraech beide Haelften
 * gebraucht werden: das Buero plant und rechnet ab, der Mitarbeiter stempelt
 * und hakt die Checkliste am Objekt ab. Wer nur das Buero zeigt, zeigt die
 * Haelfte.
 *
 * **Der Balken** ueber allem, dauerhaft und nicht wegzuklicken. Er sagt zwei
 * Dinge, und das zweite ist das wichtigere: die Daten sind erfunden, und alles,
 * was man sieht, wird je Betrieb eingerichtet. Ohne diesen Satz verkauft die
 * Vorfuehrung den falschen Eindruck, naemlich dass die Bereiche Haus, Pool und
 * Garten zu Jornado gehoerten. Sie gehoeren zum erfundenen Beispielbetrieb.
 *
 * Er ist ausdruecklich nicht wegzuklicken. Ein Hinweis, den man schliessen
 * kann, ist beim zweiten Bildschirm weg, und genau dann faengt der Interessent
 * an, das Gesehene fuer die Vorgabe zu halten.
 */

/** Einmal je Seitenaufruf gefragt und danach behalten. */
let gemerkt: { demo: boolean; codeNoetig: boolean } | null = null;

export function useDemo(): { demo: boolean; codeNoetig: boolean } | null {
  const [stand, setStand] = useState(gemerkt);
  useEffect(() => {
    if (gemerkt) return;
    void demostand().then((s) => {
      gemerkt = s;
      setStand(s);
    });
  }, []);
  return stand;
}

export function Demoeinstieg({ onSuccess }: { onSuccess: (u: SessionUser) => void }) {
  const { t } = useTranslation();
  const stand = useDemo();
  const [code, setCode] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  if (!stand?.demo) return null;

  async function oeffnen(rolle: "buero" | "mitarbeiter") {
    setLaeuft(rolle);
    setFehler(null);
    try {
      onSuccess(await demoOeffnen(rolle, code));
    } catch (e) {
      setFehler(fehlertext(e, t("demo.fehler")));
    } finally {
      setLaeuft(null);
    }
  }

  return (
    <div className="mt-6 rounded-card bg-white p-6 shadow-sm">
      <h2 className="text-lg font-bold">{t("demo.titel")}</h2>
      <p className="mt-1 text-sm text-leise">{t("demo.einstiegHinweis")}</p>

      {stand.codeNoetig && (
        <div className="mt-4">
          <label className="mb-1 block text-sm font-medium">{t("demo.code")}</label>
          <input
            value={code} onChange={(e) => setCode(e.target.value)}
            className="w-full rounded border border-linie/40 px-3 py-2 outline-none focus:border-marke"
          />
        </div>
      )}

      {fehler && <p className="mt-3 text-sm text-red-700">{fehler}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        <Knopf type="button" disabled={laeuft !== null} onClick={() => void oeffnen("buero")}>
          {laeuft === "buero" ? t("login.pending") : t("demo.alsBuero")}
        </Knopf>
        <Knopf
          type="button" art="neben" disabled={laeuft !== null}
          onClick={() => void oeffnen("mitarbeiter")}
        >
          {laeuft === "mitarbeiter" ? t("login.pending") : t("demo.alsMitarbeiter")}
        </Knopf>
      </div>
    </div>
  );
}

/**
 * Der Balken ueber der angemeldeten Oberflaeche.
 *
 * Er nimmt Platz weg, und das ist beabsichtigt. Ein Hinweis, der sich in eine
 * Ecke zurueckzieht, wird nicht gelesen, und dieser hier muss gelesen werden:
 * er ist der Unterschied zwischen "das kann Jornado" und "das ist Jornado bei
 * einem anderen Betrieb".
 */
export function Demobalken() {
  const { t } = useTranslation();
  const stand = useDemo();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (!stand?.demo) return null;

  async function zuruecksetzen() {
    if (laeuft) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await api.post("/demo/zuruecksetzen", {});
      /*
       * Neu laden statt den Zustand im Browser aufzuraeumen. Das Zuruecksetzen
       * loescht auch die Sitzungen; jeder Versuch, danach mit dem alten Token
       * weiterzuarbeiten, endete in einer Reihe von Fehlermeldungen. Ein
       * vollstaendiger Neustart der Seite ist hier das Ehrlichste.
       */
      window.location.href = "/";
    } catch (e) {
      setFehler(fehlertext(e, t("demo.fehler")));
      setLaeuft(false);
    }
  }

  return (
    <div className="border-b border-marke/30 bg-marke/10 px-4 py-2 text-sm">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1">
        <span className="rounded bg-marke px-2 py-0.5 text-xs font-bold text-white">
          {t("demo.marke")}
        </span>
        <span className="grow">{t("demo.balken")}</span>
        {fehler && <span className="text-red-700">{fehler}</span>}
        <button
          type="button" onClick={() => void zuruecksetzen()} disabled={laeuft}
          className="shrink-0 text-xs font-semibold underline underline-offset-2 disabled:opacity-60"
        >
          {laeuft ? t("demo.setztZurueck") : t("demo.zuruecksetzen")}
        </button>
      </div>
    </div>
  );
}
